// ============ Tenant Store - Gestione Stato Tenant Attivo ============

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiClient } from '../api/client';
import type { OrgBranding } from '../components/common/OrgAvatar';
import { browserTimeZone } from '../lib/tenantTime';

// =============================================================================
// Types
// =============================================================================

export interface TenantMembership {
  organizationId: string;
  tenantName: string;
  tenantSlug: string;
  /// Timezone IANA dell'organizzazione. Le memberships salvate prima del
  /// multi-timezone non ce l'hanno: i consumer ricadono sul fuso del browser.
  timezone?: string;
  role: 'OWNER' | 'ADMIN' | 'WORKER';
  /// Identità visiva dell'organizzazione (opzionale: le memberships salvate
  /// prima dell'introduzione del branding non ce l'hanno)
  branding?: OrgBranding;
}

interface TenantState {
  // State
  activeTenantId: string | null;
  activeTenantName: string | null;
  activeTenantSlug: string | null;
  /// Fuso in cui vanno mostrati TUTTI gli orari del gestionale: il calendario
  /// e' dell'attivita', non di chi lo guarda.
  activeTenantTimezone: string | null;
  activeRole: 'OWNER' | 'ADMIN' | 'WORKER' | null;
  memberships: TenantMembership[];

  // Actions
  setMemberships: (memberships: TenantMembership[]) => void;
  selectTenant: (membership: TenantMembership) => void;
  setActiveTenantTimezone: (timezone: string) => void;
  clearTenant: () => void;
}

// =============================================================================
// Store
// =============================================================================

export const useTenantStore = create<TenantState>()(
  persist(
    (set) => ({
      // Initial State
      activeTenantId: null,
      activeTenantName: null,
      activeTenantSlug: null,
      activeTenantTimezone: null,
      activeRole: null,
      memberships: [],

      // Imposta le memberships (dopo login)
      setMemberships: (memberships: TenantMembership[]) => {
        set((state) => {
          // Le memberships appena scaricate portano il timezone aggiornato:
          // se una riguarda il tenant attivo, il fuso in uso si riallinea
          // (l'admin puo' averlo cambiato da un'altra sessione).
          const active = state.activeTenantId
            ? memberships.find((m) => m.organizationId === state.activeTenantId)
            : undefined;
          return {
            memberships,
            ...(active?.timezone && { activeTenantTimezone: active.timezone }),
          };
        });
      },

      // Seleziona un tenant attivo
      selectTenant: (membership: TenantMembership) => {
        // Imposta header di default per tutte le chiamate API
        apiClient.defaults.headers.common['x-tenant-id'] =
          membership.organizationId;

        set({
          activeTenantId: membership.organizationId,
          activeTenantName: membership.tenantName,
          activeTenantSlug: membership.tenantSlug,
          activeTenantTimezone: membership.timezone ?? null,
          activeRole: membership.role,
        });
      },

      // Aggiornamento del fuso dalle Impostazioni, senza rifare il login
      setActiveTenantTimezone: (timezone: string) => {
        set((state) => ({
          activeTenantTimezone: timezone,
          memberships: state.memberships.map((m) =>
            m.organizationId === state.activeTenantId ? { ...m, timezone } : m,
          ),
        }));
      },

      // Reset tenant (logout o cambio)
      clearTenant: () => {
        delete apiClient.defaults.headers.common['x-tenant-id'];

        set({
          activeTenantId: null,
          activeTenantName: null,
          activeTenantSlug: null,
          activeTenantTimezone: null,
          activeRole: null,
          memberships: [],
        });
      },
    }),
    {
      name: 'skedulab-tenant',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        activeTenantId: state.activeTenantId,
        activeTenantName: state.activeTenantName,
        activeTenantSlug: state.activeTenantSlug,
        activeTenantTimezone: state.activeTenantTimezone,
        activeRole: state.activeRole,
        memberships: state.memberships,
      }),
      onRehydrateStorage: () => {
        return (state: TenantState | undefined) => {
          if (state?.activeTenantId) {
            apiClient.defaults.headers.common['x-tenant-id'] =
              state.activeTenantId;
          }
        };
      },
    },
  ),
);

// =============================================================================
// Selectors
// =============================================================================

export const useActiveTenantId = () =>
  useTenantStore((s) => s.activeTenantId);
export const useActiveTenantName = () =>
  useTenantStore((s) => s.activeTenantName);
export const useActiveRole = () => useTenantStore((s) => s.activeRole);
export const useMemberships = () => useTenantStore((s) => s.memberships);
export const useTenantActions = () =>
  useTenantStore((s) => ({
    setMemberships: s.setMemberships,
    selectTenant: s.selectTenant,
    setActiveTenantTimezone: s.setActiveTenantTimezone,
    clearTenant: s.clearTenant,
  }));

/**
 * Variante non-reattiva di useTenantTimeZone, per il codice fuori dai componenti
 * (store, helper). Legge lo stesso valore senza sottoscrivere aggiornamenti.
 */
export const getTenantTimeZone = (): string =>
  useTenantStore.getState().activeTenantTimezone ?? browserTimeZone();

/**
 * Fuso in cui rendere gli orari del gestionale.
 *
 * Fallback sul fuso del browser quando il tenant non ne ha ancora uno noto
 * (sessione precedente al multi-timezone, o memberships non ancora caricate):
 * e' il comportamento storico, quindi non introduce salti visibili.
 */
export const useTenantTimeZone = (): string =>
  useTenantStore((s) => s.activeTenantTimezone) ?? browserTimeZone();
